/**
 * Type definitions for API responses and errors
 */

import { AxiosError } from 'axios';

/**
 * User data from the UserMigration API
 */
export interface UserApiResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string | null;
  workspaceId?: string | null;
  applicationSubscription?: {
    id: string;
    organisation?: {
      name: string;
    };
  } | null;
}

/**
 * Response from CreateUserMigrationWizardUrl endpoint
 */
export interface CreateMigrationWizardResponse {
  data: {
    createUserMigrationWizardUrl: {
      userMigrationWizardUrl: string;
    };
  };
}

/**
 * Type guard for CreateMigrationWizardResponse
 */
export function isCreateMigrationWizardResponse(data: unknown): data is CreateMigrationWizardResponse {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const d = data as Record<string, unknown>;
  if (!d.data || typeof d.data !== 'object') {
    return false;
  }
  const inner = d.data as Record<string, unknown>;
  if (!inner.createUserMigrationWizardUrl || typeof inner.createUserMigrationWizardUrl !== 'object') {
    return false;
  }
  const wizard = inner.createUserMigrationWizardUrl as Record<string, unknown>;
  return typeof wizard.userMigrationWizardUrl === 'string';
}

/**
 * Standard API error structure
 */
export interface ApiErrorResponse {
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

/**
 * Type guard to check if an error is an Axios error
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return error !== null && 
         typeof error === 'object' && 
         'isAxiosError' in error &&
         (error as AxiosError).isAxiosError === true;
}

/**
 * Extract error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}

/**
 * Get detailed error information from an Axios error
 */
export function getApiErrorDetails(error: unknown): {
  message: string;
  status?: number;
  code?: string;
} {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const responseData = error.response?.data as ApiErrorResponse | undefined;
    
    if (status === 401) {
      return { message: 'Authentication failed. Please sign in again.', status };
    }
    if (status === 403) {
      return { message: 'You do not have permission to perform this action.', status };
    }
    if (status === 404) {
      return { message: 'The requested resource was not found.', status };
    }
    if (status && status >= 500) {
      return { message: `Server error (${status}). Please try again later.`, status };
    }
    if (responseData?.message) {
      return { message: responseData.message, status, code: responseData.code };
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return { message: 'Request timed out. Please check your connection and try again.', code: error.code };
    }
    if (!error.response) {
      return { message: 'Cannot connect to server. Please check your connection.' };
    }
  }
  
  return { message: getErrorMessage(error) };
}
